import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { CrmSubNav } from '@/components/crm/CrmSubNav';
import { CrmEmpresaSelector } from '@/components/crm/CrmEmpresaSelector';
import { useCrmEmpresaAtiva } from '@/hooks/useCrmEmpresaAtiva';
import { crmApiKeysService } from '@/services/crmApi';
import { CrmApiKey, CrmApiKeyCriada } from '@/types/crm';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, KeyRound, Trash2, Loader2, Copy, Check, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export default function CrmApiKeys() {
  const { user } = useAuth();
  const isAdmin = user?.tipo === 'admin' || user?.tipo === 'superadmin';
  const { isSuperAdmin, empresas, selectedEmpresaId, setSelectedEmpresaId, empresaId } = useCrmEmpresaAtiva();

  const [chaves, setChaves] = useState<CrmApiKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [nome, setNome] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [chaveCriada, setChaveCriada] = useState<CrmApiKeyCriada | null>(null);
  const [copiado, setCopiado] = useState(false);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const loadData = async () => {
    if ((isSuperAdmin && !empresaId) || !isAdmin) {
      setChaves([]);
      setIsLoading(false);
      return;
    }
    try {
      setIsLoading(true);
      const result = await crmApiKeysService.getAll(isSuperAdmin ? empresaId : undefined);
      if (result.success) {
        setChaves(result.data);
      }
    } catch (error) {
      console.error('Error loading crm api keys:', error);
      toast.error('Erro ao carregar chaves de API');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [empresaId]);

  const handleCreate = async () => {
    if (!nome.trim()) return;
    try {
      setIsSaving(true);
      const result = await crmApiKeysService.create({
        nome: nome.trim(),
        empresa_id: isSuperAdmin ? empresaId : undefined,
      });
      if (result.success) {
        setChaveCriada(result.data);
        setNome('');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao criar chave de API');
      }
    } catch (error) {
      toast.error('Erro ao criar chave de API');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      const result = await crmApiKeysService.delete(deleteConfirmId, isSuperAdmin ? empresaId : undefined);
      if (result.success) {
        toast.success('Chave revogada');
        loadData();
      } else {
        toast.error(result.error || 'Erro ao revogar chave');
      }
    } catch (error) {
      toast.error('Erro ao revogar chave');
    }
    setDeleteConfirmId(null);
  };

  const closeCreateDialog = () => {
    setIsDialogOpen(false);
    setChaveCriada(null);
    setCopiado(false);
    setNome('');
  };

  const copiarToken = async () => {
    if (!chaveCriada) return;
    try {
      await navigator.clipboard.writeText(chaveCriada.token);
      setCopiado(true);
      toast.success('Token copiado');
    } catch {
      toast.error('Não foi possível copiar automaticamente — selecione e copie manualmente');
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="animate-fade-in space-y-6">
        <CrmSubNav
          active="integracoes"
          right={
            isSuperAdmin ? (
              <CrmEmpresaSelector empresas={empresas} value={selectedEmpresaId} onChange={setSelectedEmpresaId} />
            ) : undefined
          }
        />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Integrações</h1>
            <p className="text-muted-foreground">
              Tokens de API para integrar sistemas externos ao CRM — protótipo.{' '}
              <a href="/api/docs" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                Ver documentação da API <ExternalLink className="h-3 w-3" />
              </a>
            </p>
          </div>
          {isAdmin && (
            <Button onClick={() => setIsDialogOpen(true)} disabled={isSuperAdmin && !empresaId}>
              <Plus className="mr-2 h-4 w-4" />
              Nova Chave
            </Button>
          )}
        </div>

        {isSuperAdmin && !empresaId ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Selecione uma empresa acima para ver e gerenciar as chaves de API do CRM
            </CardContent>
          </Card>
        ) : !isAdmin ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Apenas administradores podem gerenciar chaves de API.
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Chave</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Último uso</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {chaves.map((chave) => (
                    <TableRow key={chave.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <KeyRound className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{chave.nome}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">{chave.key_prefix}…</TableCell>
                      <TableCell>
                        <Badge variant={chave.ativa ? 'default' : 'secondary'}>
                          {chave.ativa ? 'Ativa' : 'Revogada'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {chave.ultima_utilizacao_em ? new Date(chave.ultima_utilizacao_em).toLocaleString('pt-BR') : 'Nunca usada'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => setDeleteConfirmId(chave.id)}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}

                  {chaves.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                        Nenhuma chave de API criada ainda
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={(open) => !open && closeCreateDialog()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{chaveCriada ? 'Chave criada' : 'Nova Chave de API'}</DialogTitle>
          </DialogHeader>

          {chaveCriada ? (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Copie o token agora — ele não será exibido novamente. Se perder, será preciso criar uma nova chave.
              </p>
              <div className="flex items-center gap-2">
                <Input readOnly value={chaveCriada.token} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
                <Button variant="outline" size="icon" onClick={copiarToken}>
                  {copiado ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Use no header <code>Authorization: Bearer &lt;token&gt;</code> contra <code>/api/v1/crm/...</code>.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="nome-chave">Nome</Label>
                <Input
                  id="nome-chave"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Integração Zapier"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            {chaveCriada ? (
              <Button onClick={closeCreateDialog}>Concluir</Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeCreateDialog}>
                  Cancelar
                </Button>
                <Button onClick={handleCreate} disabled={!nome.trim() || isSaving}>
                  {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Criar
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revogar chave de API</AlertDialogTitle>
            <AlertDialogDescription>
              Qualquer integração usando esse token para de funcionar imediatamente. Esta ação não pode ser
              desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Revogar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
